import BottomSheet from './BottomSheet';
import type { ReachViewer } from '@/types';

/**
 * my-reco-rebound-clarity — who made the sound travel, and who found it through
 * them.
 *
 * The card's chain stays one fixed-height row; the topology lives here, where
 * height is free. That split is the whole design: a 390px row cannot hold
 * `[Moi] → discoverers → [rebounder] → their discoverers` without either
 * truncating it into meaninglessness or growing with the number of rebounders.
 *
 * Each block is one rebounder with THEIR discoverers indented underneath —
 * never a flat list, because "4 people discovered it via a rebound" does not
 * tell the author who made that happen, which is the valuable half.
 */

export interface ReboundChain {
  rebounder: ReachViewer | null;
  viewers: ReachViewer[];
  viewersCount: number;
}

const VIEWERS_PER_CHAIN = 10;

function Avatar({ user, size }: { user: ReachViewer; size: number }) {
  if (user.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt=""
        style={{ width: size, height: size }}
        className="tw:shrink-0 tw:rounded-full tw:object-cover"
      />
    );
  }
  return (
    <span
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.34) }}
      className="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary tw:font-semibold tw:text-white"
    >
      {(user.username ?? '?').slice(0, 2).toUpperCase()}
    </span>
  );
}

export default function ReboundChainsSheet({
  open,
  title,
  chains,
  moreLabel,
  emptyChainLabel,
  onClose,
}: {
  open: boolean;
  title: string;
  chains: ReboundChain[];
  moreLabel: (n: number) => string;
  /** Shown under a rebounder nobody has listened to yet. */
  emptyChainLabel: string;
  onClose: () => void;
}) {
  return (
    <BottomSheet open={open} title={title} onClose={onClose} testid="rebound-chains-sheet">
      <ul className="tw:flex tw:flex-col tw:gap-4">
        {chains.map((chain, i) => {
          const shown = chain.viewers.slice(0, VIEWERS_PER_CHAIN);
          const remaining = chain.viewersCount - shown.length;
          return (
            <li
              key={chain.rebounder?.id ?? i}
              data-testid={`rebound-chain-${chain.rebounder?.id ?? i}`}
            >
              {chain.rebounder && (
                <div className="tw:flex tw:items-center tw:gap-3">
                  <Avatar user={chain.rebounder} size={36} />
                  <span className="tw:font-semibold tw:text-white">{chain.rebounder.username}</span>
                </div>
              )}

              <div className="tw:ml-5 tw:mt-1 tw:border-l tw:border-grey-4/40 tw:pl-4">
                {shown.length === 0 ? (
                  <p className="tw:py-1 tw:text-[13px] tw:text-grey-3">{emptyChainLabel}</p>
                ) : (
                  <ul className="tw:flex tw:flex-col">
                    {shown.map((v) => (
                      <li
                        key={v.id}
                        data-testid="rebound-chain-viewer"
                        className="tw:flex tw:items-center tw:gap-2 tw:py-1"
                      >
                        <Avatar user={v} size={26} />
                        <span className="tw:text-[13px] tw:text-grey-2">{v.username}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {remaining > 0 && (
                  <p className="tw:pt-1 tw:text-[13px] tw:text-grey-3">{moreLabel(remaining)}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </BottomSheet>
  );
}
